import { useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession, canManage } from '../lib/session';
import { ART14, ART14_ARCHIVED } from '../lib/fixtures';
import { apiFetch, useResource, useResourceMapped } from '../lib/api';
import { mapArt14, type ApiArt14Row } from '../lib/adapt';
import { buildLog, type AuditRow, type LogLine } from '../lib/audit-log';
import { docToBlobParts, groupDocs, levelForStage, FIELD_SHEET_NAME, type PackageResult, type ServerQ16Field } from '../lib/report-docs';
import { useMembers } from '../lib/members';
import type { Art14Row, Member, ReportStageKey, ReportStatus } from '../lib/contract';
import { shortUtc } from '../lib/contract';
import { stagesFor, MANUAL_STATUSES, CISA_JUSTIFICATIONS, VEX_STATUSES } from '../lib/cra';
import { q16Fields, type Q16Field } from '../lib/q16';
import { Badge, Btn, Card, Field, SectionHead, Tabs } from '../components/ui/primitives';
import { Countdown, FrictionPanel, useToast } from '../components/ui/widgets';
import { RichEditor } from '../components/ui/RichEditor';
import { UserCombo } from '../components/ui/UserCombo';

/**
 * Stage-due form fields, each mapped to the Q16 field it FILLS (q16v for the
 * vulnerability branch, q16i for incidents) — typing here persists a Q16
 * override, so the form, the Q16 tab, the generated package and the Sent gate
 * always carry the same value.
 */
type StageField = { label: string; type: 'rich' | 'date'; val?: string; hint?: string; q16v?: string; q16i?: string };

const STAGE_FIELDS: Record<string, StageField[]> = {
  '24h': [],
  '72h': [
    { label: 'General nature of the vulnerability', type: 'rich', hint: 'e.g. memory corruption, auth bypass — non-sensitive phrasing', q16v: 'v16', q16i: 'i17' },
    { label: 'Corrective / mitigating measures taken', type: 'rich', hint: 'Patch or mitigation you issued', q16v: 'v18', q16i: 'i18' },
    { label: 'Corrective / mitigating measures users can take', type: 'rich', hint: 'Workarounds, configuration, monitoring', q16v: 'v19', q16i: 'i19' },
    { label: 'Date the corrective measure became available', type: 'date', hint: 'sets the 14-day final-report clock (Q16 v21)' },
  ],
  '14d': [
    { label: 'Full description of the vulnerability', type: 'rich', hint: 'Complete technical description', q16v: 'v22' },
    { label: 'Impact of the vulnerability', type: 'rich', hint: 'Affected functionality / data exposure', q16v: 'v24' },
    { label: 'Details of the security update', type: 'rich', hint: 'Hashes, URLs, signing keys of the fix', q16v: 'v26' },
  ],
  '1m': [
    { label: 'Detailed description of the incident', type: 'rich', q16i: 'i21' },
    { label: 'Impact of the incident', type: 'rich', q16i: 'i23' },
    { label: 'Type of threat / root cause', type: 'rich', q16i: 'i24' },
    { label: 'Applied and ongoing mitigations', type: 'rich', q16i: 'i25' },
  ],
  complete: [],
};

const USE_FIXTURES = (import.meta.env.VITE_USE_FIXTURES as string | undefined) !== 'false';

/** Seed a RichEditor with a persisted plain-text override (escaped, one <p>). */
function seedHtml(v: string | undefined): string {
  if (!v?.trim()) return '';
  return `<p>${v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`;
}

/** The stage-submission endpoint that pairs with marking a report Sent. */
function stageSubmitAction(key: ReportStageKey): 'notified' | 'follow-up' | 'final-report' {
  if (key === '24h') return 'notified';
  if (key === '72h') return 'follow-up';
  return 'final-report'; // 14d · 1m · complete → final report
}

export function ReportEditor() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { session } = useSession();
  // Archived/deleted incidents live in a different query — the Archived view links
  // here with ?archived=1 so the report page can still open them.
  const archived = sp.get('archived') === '1';
  const live = useResourceMapped<ApiArt14Row[], Art14Row[]>(session, USE_FIXTURES ? null : `/api/art14?fresh=1${archived ? '&archived=1' : ''}`, (rows) => rows.map(mapArt14));
  const { members } = useMembers(session.organizationId);
  const ev = USE_FIXTURES ? ([...ART14, ...ART14_ARCHIVED].find((a) => a.id === id) ?? ART14[0]) : live.data?.find((a) => a.id === id);
  if (!USE_FIXTURES && live.loading) return <div className="hint" style={{ padding: 24 }}>Loading incident…</div>;
  if (!ev) {
    return (
      <div className="hint" style={{ padding: 24 }}>
        Incident not found. <Link to="/app/incidents">Back to Incidents →</Link>
      </div>
    );
  }
  // Key by id so all report state resets when navigating between incidents.
  return <ReportEditorInner key={ev.id} ev={ev} members={members} reload={live.reload} />;
}

function ReportEditorInner({ ev, members, reload }: { ev: Art14Row; members: Member[]; reload: () => void }) {
  const { session } = useSession();
  const [toastNode, showToast] = useToast();
  const stages = useMemo(() => stagesFor(ev.kind), [ev.kind]);

  const initialStage = Math.max(0, stages.findIndex((s) => s.key === ev.milestone));
  const [stageIdx, setStageIdx] = useState(initialStage < 0 ? 0 : initialStage);
  const [status, setStatus] = useState<ReportStatus>(ev.reportStatus);
  // Resolve the assignee REACTIVELY from the members directory: it loads async
  // (BetterAuth), so freezing it in useState at mount left "Assigned to" blank
  // forever. Track the ref (editable via Reassign); derive the Member on render.
  const [assigneeRef, setAssigneeRef] = useState<string | null>(ev.assigneeUserRef);
  const assignee = members.find((m) => m.id === assigneeRef) ?? null;
  const [acked, setAcked] = useState<boolean>(ev.acknowledgedAt !== null);
  const [fixAvailable, setFixAvailable] = useState<string | null>(ev.fixAvailableAt);
  const [usersInformed, setUsersInformed] = useState<boolean>(ev.usersInformedAt !== null);
  const [tab, setTab] = useState<'fill' | 'q16'>('fill');
  const [q16Editing, setQ16Editing] = useState(false);
  const [generated, setGenerated] = useState<boolean>(status === 'Sent');
  const [pkg, setPkg] = useState<PackageResult | null>(null); // documents from POST …/package
  const [genError, setGenError] = useState<string | null>(null); // live: package generation failed

  // ── Q16 sync (user report 2026-07-19) ──
  // ONE store for user-entered Q16 values: seeded from the persisted overrides,
  // updated on every keystroke (synchronous UI), POSTed on blur. The package
  // builder overlays the same store server-side, so the form, the Q16 tab, the
  // generated package and the Sent gate can never disagree.
  const [q16Local, setQ16Local] = useState<Record<string, string>>(() => ({ ...(ev.q16Overrides ?? {}) }));
  function saveQ16(field: string, plain: string, done: boolean) {
    setQ16Local((m) => ({ ...m, [field]: plain }));
    if (done) void postAction('q16', { field, value: plain });
  }
  /** The stage field's Q16 id for THIS event's branch (vulnerability vs incident). */
  const q16IdFor = (f: StageField): string | undefined => (ev.kind === 'incident' ? f.q16i : f.q16v);
  /** missingMandatory, minus fields the user has since filled locally — live view. */
  const missingNow = (pkg?.missingMandatory ?? []).filter((m) => !q16Local[m.field]?.trim());

  // ── Action log ──
  // Fixtures: the static demo seed, exactly as before. Live: the seed is empty —
  // the log is server audit rows + derived System milestones (below), plus the
  // optimistic entries addLog appends this session (server rows win on reload).
  const [localLog, setLocalLog] = useState<LogLine[]>(() => (USE_FIXTURES ? [
    { when: shortUtc(ev.detectedAt), who: 'System', what: 'opened incident from CISA KEV match' },
    { when: shortUtc(ev.detectedAt), who: 'System', what: `auto-assigned to ${assignee?.name ?? 'Incident Manager'}` },
    { when: shortUtc(ev.detectedAt), who: 'System', what: 'prepared the Q16 fields for manual SRP web-form entry' },
  ] : []));
  const audit = useResource<AuditRow[]>(session, USE_FIXTURES ? null : `/api/audit?entityType=art14_event&entityId=${ev.id}`);
  const serverLog = useMemo<LogLine[]>(() => (USE_FIXTURES ? [] : buildLog(ev, audit.data ?? [])), [ev, audit.data]);
  const log: LogLine[] = [...serverLog, ...localLog];

  // panels
  const [reassignOpen, setReassignOpen] = useState(false);
  const [pendingAssignee, setPendingAssignee] = useState<Member | null>(null);
  const [pendingStage, setPendingStage] = useState<number | null>(null);
  const [sentOpen, setSentOpen] = useState(false);
  const [prevStatus, setPrevStatus] = useState<ReportStatus>(status);
  const [vexOpen, setVexOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const navigate = useNavigate();

  const t0 = Date.parse(ev.detectedAt);
  const ctx = { t0, fixAvailableAt: fixAvailable ? Date.parse(fixAvailable) : null, notified72At: ev.notifiedAt ? Date.parse(ev.notifiedAt) : null };
  const stage = stages[stageIdx];
  const deadline = stage.deadline(ctx);
  const from72 = stageIdx >= 1;

  const addLog = (what: string) => setLocalLog((l) => [...l, { when: 'now', who: session.userName, what }]);

  // Last server error surfaced by postAction — read by generate() for the inline state.
  const lastActionError = useRef<string | null>(null);

  // Live-mode POST to /api/art14/:id/<sub>; no-op in fixtures. Never fails silently:
  // a non-OK response surfaces the server's {error} body when present (e.g. the 422
  // from completing a stage without users informed), falling back to the status code.
  async function postAction(sub: string, body?: unknown): Promise<Response | null> {
    if (USE_FIXTURES) return null;
    try {
      const r = await apiFetch(session, `/api/art14/${ev.id}/${sub}`, { method: 'POST', body: JSON.stringify(body ?? {}) });
      if (!r.ok) {
        let msg = `Action failed (${r.status})`;
        try {
          const parsed = (await r.clone().json()) as { error?: unknown };
          if (typeof parsed.error === 'string' && parsed.error.trim()) msg = parsed.error;
        } catch { /* non-JSON error body — keep the status-code message */ }
        lastActionError.current = msg;
        showToast(msg);
        return null;
      }
      return r;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      lastActionError.current = msg;
      showToast(msg);
      return null;
    }
  }

  // Download ONE generated document (any pkg.documents entry, or the field sheet).
  function downloadDoc(fname: string, source: PackageResult | null = pkg) {
    const parts = docToBlobParts(fname, source);
    if (!parts) { showToast('Document not available yet — generate the report first.'); return; }
    const url = URL.createObjectURL(new Blob([parts.text], { type: parts.mime }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // ── Archive (soft, retained) / Delete (hard, non-notified only) ──
  async function confirmArchive() {
    setArchiveOpen(false);
    if (!USE_FIXTURES) { const r = await postAction('archive'); if (!r) return; }
    showToast('Incident archived — retained in the audit log.');
    navigate('/app/incidents');
  }
  async function confirmDelete() {
    setDeleteOpen(false);
    if (!USE_FIXTURES) {
      const r = await apiFetch(session, `/api/art14/${ev.id}`, { method: 'DELETE' });
      if (!r.ok) {
        let msg = `Delete failed (${r.status})`;
        try { const b = (await r.clone().json()) as { error?: string }; if (b.error) msg = b.error; } catch { /* keep status message */ }
        showToast(msg);
        return;
      }
    }
    showToast('Incident deleted.');
    navigate('/app/incidents');
  }

  // ── Acknowledgement (R-INC-02) ──
  function acknowledge() { setAcked(true); addLog('acknowledged reception'); void postAction('ack'); }
  function confirmReassign() {
    if (!pendingAssignee) return;
    setAssigneeRef(pendingAssignee.id); setAcked(false); setReassignOpen(false);
    addLog(`reassigned incident to ${pendingAssignee.name}`);
    void postAction('reassign', { assigneeUserRef: pendingAssignee.id });
    setPendingAssignee(null);
  }

  // ── Stage change (friction; C-1/C-2; C-4 blocks completion) ──
  // Live: the POST persists FIRST — a non-OK response (e.g. the 422 completion
  // guard) leaves the local stage untouched, so the UI never runs ahead of the server.
  async function confirmStage() {
    const target = pendingStage!;
    if (stages[target].key === 'complete' && !usersInformed) { showToast('Art.14(8): mark impacted users informed before completing.'); setPendingStage(null); return; }
    if (!USE_FIXTURES) {
      const r = await postAction('stage', { stage: stages[target].key });
      if (!r) { setPendingStage(null); return; } // POST failed → roll back (keep current stage)
    }
    setStageIdx(target); setStatus('New'); setPendingStage(null);
    // A generated package is LEVEL-specific (Q16 field 2) — a stage change makes it stale.
    setGenerated(false); setPkg(null); setGenError(null);
    addLog(`changed stage → ${stages[target].label} · status → New`);
  }

  // ── Generate the SRP prep for THIS notification level (Q16 field 2) ──
  async function generate() {
    setGenerated(true);
    setGenError(null);
    if (!USE_FIXTURES) {
      lastActionError.current = null;
      const r = await postAction('package', { level: levelForStage(stage.key) });
      if (!r) { setGenError(lastActionError.current ?? 'server error'); return; }
      try {
        setPkg((await r.json()) as PackageResult);
      } catch {
        setGenError('unexpected (non-JSON) response from the server');
        return;
      }
    }
    setStatus('Pending review');
    addLog('generated the ENISA SRP submission (manual fill guide + CSAF) · status → Pending review');
    await postAction('report-status', { status: 'Pending review' });
  }

  // ── Status → Sent (friction; stops clock + auto-advances stage; U-1) ──
  // Live-mode gate (ENISA rules): a report can only be marked Sent after the
  // package for THIS level was generated and its mandatory Q16 fields are filled
  // — an SRP submission missing X-flagged fields is non-compliant, not merely
  // incomplete. Fixtures mode keeps the free demo flow.
  function onStatusChange(v: string) {
    if (v === 'Sent') {
      if (!USE_FIXTURES) {
        if (!pkg) { showToast('Generate the SRP report for this stage first.'); return; }
        if (missingNow.length > 0) {
          showToast(`Not ready to submit — ${missingNow.length} mandatory Q16 field(s) for this stage still empty: ${missingNow.map((m) => m.field).join(', ')}`);
          return;
        }
      }
      setPrevStatus(status); setSentOpen(true);
    }
    else { setStatus(v as ReportStatus); addLog(`status → ${v}`); void postAction('report-status', { status: v }); }
  }
  async function confirmSent() {
    setSentOpen(false);
    addLog(`status → Sent · submitted ${stage.label} to ENISA SRP; countdown & pings stopped`);
    // Persist the Sent status, then fire the stage-appropriate SRP submission.
    await postAction('report-status', { status: 'Sent' });
    await postAction(stageSubmitAction(stage.key));
    const next = stageIdx + 1;
    if (next < stages.length) {
      if (stages[next].key === 'complete' && !usersInformed) { setStatus('Sent'); showToast('Art.14(8): mark impacted users informed before completing.'); return; }
      setStageIdx(next); setStatus('New'); setGenerated(false); setPkg(null); setGenError(null);
      addLog(`stage auto-advanced → ${stages[next].label}`);
      // Keep the server's report status in sync with the local reset.
      await postAction('report-status', { status: 'New' });
    } else { setStatus('Sent'); }
  }

  const statusOptions: ReportStatus[] = status === 'Pending review' ? ['Pending review', ...MANUAL_STATUSES] : MANUAL_STATUSES;

  // Q16 tab rows. Fixtures: the demo prefill (fabricated manufacturer is fine
  // there). Live: the SERVER's q16 once a package was generated (the authority —
  // real org profile, level fields, overrides), else the local prefill with the
  // org name, never a fabricated one. q16Local overlays both, so an edit shows
  // everywhere instantly.
  const q16Base: Q16Field[] = useMemo(() => {
    if (!USE_FIXTURES && pkg?.q16 && Array.isArray(pkg.q16)) {
      return (pkg.q16 as ServerQ16Field[]).map((f) => ({
        n: f.field, label: f.label,
        h24: (f.milestones?.h24 ?? '-') as Q16Field['h24'], h72: (f.milestones?.h72 ?? '-') as Q16Field['h72'], fin: (f.milestones?.final ?? '-') as Q16Field['fin'],
        src: f.source === 'auto' ? 'auto' : 'you',
        val: f.value ?? undefined, hint: f.hint,
      }));
    }
    return q16Fields({
      kind: ev.kind, cve: ev.cveId, product: `${ev.productName} ${ev.versionLabel}`,
      manufacturer: USE_FIXTURES ? 'ACME Industrial GmbH' : session.orgName,
      reporter: session.userName, markets: ev.markets,
      cvss: ev.cvss, severity: ev.cvss !== null && ev.cvss >= 9 ? 'critical' : 'high',
      title: `Actively exploited ${ev.cveId} in ${ev.productName} ${ev.versionLabel}`,
    });
  }, [pkg, ev, session.orgName, session.userName]);
  const q16: Q16Field[] = q16Base.map((f) => (q16Local[f.n]?.trim() ? { ...f, val: q16Local[f.n], src: 'you' } : f));

  return (
    <>
      {toastNode}
      <div>
        <div className="crumbs">
          <Link to="/app/incidents">Incidents</Link> ／{' '}
          {ev.productId
            ? <Link to={`/app/products/${ev.productId}`}>{ev.productName}{ev.versionLabel ? ` ${ev.versionLabel}` : ''}</Link>
            : <span>{ev.productName}{ev.versionLabel ? ` ${ev.versionLabel}` : ''}</span>}
          {' '}／ {ev.cveId}
        </div>
        <div className="pagehead">
          <div>
            <div className="hero-eyebrow" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {ev.isKev && <Badge kind="kev">[K] KEV</Badge>}
              <span className="mono" style={{ fontSize: 'calc(13px*var(--fs))' }}>{ev.cveId}</span>
              <Badge kind="crit">{ev.kind === 'incident' ? 'Severe incident' : 'Art.14'} · {stage.label}</Badge>
            </div>
            <h1 style={{ fontSize: 'calc(24px*var(--fs))' }}>{ev.component} — {ev.productName} {ev.versionLabel}</h1>
            <div className="sub">owner {assignee?.name ?? 'unassigned'}</div>
          </div>
          <Countdown deadlineMs={deadline} cap={stage.cap} capWaiting={stage.capWaiting} t0Iso={ev.detectedAt} />
        </div>
      </div>

      {/* Assignee bar — every incident is assigned (R-INC-01/02) */}
      <Card style={{ padding: '14px 18px' }}>
        <div className="assign-row">
          <div className="assign-who">
            <span className="avatar">{(assignee?.name ?? '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('')}</span>
            <div><div className="assign-name" data-testid="assignee-name">{assignee ? <>Assigned to <b>{assignee.name}</b></> : <b>Unassigned</b>}</div><div className="assign-role">{assignee?.role ?? 'assign an owner so someone acknowledges this incident'}</div></div>
          </div>
          <div className="assign-actions">
            <Badge kind={acked ? 'safe' : 'warn'}>{acked ? '✓ Acknowledged' : '⚠ Awaiting acknowledgement'}</Badge>
            <Btn sm onClick={() => setReassignOpen((o) => !o)}>Reassign</Btn>
            {!acked && <Btn sm variant="primary" onClick={acknowledge}>Acknowledge reception</Btn>}
          </div>
        </div>
        {!acked && (
          <div className="ack-warn">
            {assignee
              ? <b>{assignee.name.split(' ')[0]}, you've been assigned an actively-exploited (KEV) incident on an open deadline.</b>
              : <b>This actively-exploited (KEV) incident is unassigned and its deadline is open — assign an owner now.</b>}{' '}
            Acknowledge reception so the team knows you have it. While a 24h/72h deadline is open, BicPort pings you <b>every hour</b> until you acknowledge.
          </div>
        )}
        {reassignOpen && (
          <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <Field label="Reassign to" req hint="search people; an incident is never left unassigned">
              <div style={{ maxWidth: 420 }}><UserCombo members={members} onPick={setPendingAssignee} /></div>
            </Field>
            <div className="toolbar" style={{ marginTop: 12 }}>
              <Btn sm variant="primary" disabled={!pendingAssignee} onClick={confirmReassign}>Reassign incident</Btn>
              <Btn sm onClick={() => setReassignOpen(false)}>Cancel</Btn>
            </div>
          </div>
        )}
      </Card>

      {/* Stage track (clickable) + explicit Change-stage button (U-2) */}
      <div className="stage-track" data-testid="stage-track">
        {stages.map((s, i) => (
          <span key={s.key} data-stage={s.key} className={`st clickable${i === stageIdx ? ' now' : ''}${i < stageIdx ? ' done' : ''}`} onClick={() => { if (i !== stageIdx) setPendingStage(i); }}>
            {s.label}{i < stages.length - 1 ? ' →' : ''}
          </span>
        ))}
        <Btn sm onClick={() => setPendingStage(stageIdx === stages.length - 1 ? stageIdx : stageIdx + 1)}>Change stage…</Btn>
      </div>
      {pendingStage !== null && (
        <FrictionPanel
          title={`Change stage to ${stages[pendingStage].label}?`}
          tone={pendingStage > stageIdx + 1 ? 'critical' : pendingStage === stages.length - 1 ? 'safe' : 'medium'}
          consequence={
            stages[pendingStage].key === 'complete' && !usersInformed
              ? <><b style={{ color: 'var(--critical)' }}>Impacted users have not been marked informed — Art.14(8) is still open.</b> Complete the users-informed step below first.</>
              : pendingStage > stageIdx + 1
                ? <>You are <b>skipping</b> {stages[stageIdx + 1].label} — a mandatory step. This is recorded. The new stage's countdown and hourly pings begin.</>
                : <>Advances to {stages[pendingStage].label}. Its countdown and hourly pings begin; the report Status resets to New.</>
          }
          ackLabel="I understand this changes the reporting stage and will be recorded in the action log."
          confirmLabel="Confirm stage change"
          onConfirm={() => void confirmStage()}
          onCancel={() => setPendingStage(null)}
        />
      )}

      {archiveOpen && (
        <FrictionPanel
          title="Archive this incident?"
          tone="medium"
          consequence={<>It leaves the active incidents list and the Today feed but is <b>retained in the audit log</b> — an Art.14 record is never destroyed.</>}
          ackLabel="I understand the incident will be archived (retained, not deleted)."
          confirmLabel="Confirm archive"
          onConfirm={() => void confirmArchive()}
          onCancel={() => setArchiveOpen(false)}
        />
      )}

      {deleteOpen && (
        <FrictionPanel
          title="Delete this incident?"
          tone="critical"
          consequence={<>This manual incident was never submitted to ENISA, so it can be <b>permanently deleted</b>. This cannot be undone. (A submitted incident can only be archived.)</>}
          ackLabel="I understand this permanently deletes the incident."
          confirmLabel="Confirm delete"
          onConfirm={() => void confirmDelete()}
          onCancel={() => setDeleteOpen(false)}
        />
      )}

      <div className="rep-grid">
        <div className="repmain">
          <Card style={{ padding: 20 }}>
            <div className="section-head" style={{ marginBottom: 6 }}>
              <h3>Report · {stage.label}</h3>
              <div className="statusbox">
                <span className="stat-lbl">Status</span>
                <select className="select status-sel" value={status} onChange={(e) => onStatusChange(e.target.value)}>
                  {statusOptions.map((s) => <option key={s} value={s} disabled={s === 'Pending review'}>{s}</option>)}
                </select>
              </div>
            </div>

            {sentOpen && (
              <div style={{ margin: '4px 0 14px' }}>
                <FrictionPanel
                  title={`Mark the ${stage.label} report as Sent to ENISA?`}
                  tone="critical"
                  consequence={<>Submits this report via the ENISA SRP. <b>Stops the countdown and hourly pings for this stage, and advances the report to the next stage.</b> This cannot be undone for this stage.</>}
                  ackLabel="I confirm this report has been submitted to ENISA via the SRP."
                  confirmLabel="Confirm — mark Sent"
                  onConfirm={confirmSent}
                  onCancel={() => { setStatus(prevStatus); setSentOpen(false); }}
                />
              </div>
            )}

            <Tabs
              tabs={[{ key: 'fill' as const, label: 'What to fill' }, { key: 'q16' as const, label: <>Q16 field sheet <span className="n">info</span></> }]}
              value={tab} onChange={setTab}
            />

            {tab === 'fill' && (
              <div className="tabpane on">
                {/* C-6 — General information is NOT starred at 24h. Fills Q16 v15/i14. */}
                <Field label="General information" req={from72} hint={from72 ? 'mandatory at 72h — describe the vulnerability, its nature and any interim mitigation' : 'recommended now · mandatory at 72h'}>
                  <RichEditor
                    placeholder="Describe the vulnerability, its nature and any interim mitigation…"
                    html={USE_FIXTURES
                      ? '<p><b>Remote code execution in the TCP/IP stack (' + ev.cveId + ')</b> of ' + ev.productName + ', firmware ' + ev.versionLabel + '. Out-of-bounds write reachable from unauthenticated IPv6 traffic; <u>listed in the CISA KEV catalogue</u> as actively exploited.</p>'
                      : seedHtml(q16Local[ev.kind === 'incident' ? 'i14' : 'v15'])}
                    onTextChange={(plain, done) => saveQ16(ev.kind === 'incident' ? 'i14' : 'v15', plain, done)}
                  />
                </Field>
                <div id="stageFields">
                  {STAGE_FIELDS[stage.key].length > 0 && <SectionHead title={`Required for ${stage.label}`} />}
                  {STAGE_FIELDS[stage.key].map((f) => (
                    <Field key={f.label} label={f.label} req hint={f.hint}>
                      {f.type === 'date'
                        ? <input className="input" style={{ maxWidth: 220 }} placeholder="YYYY-MM-DD" defaultValue={fixAvailable ?? ''} onChange={(e) => { setFixAvailable(e.target.value || null); if (e.target.value) { addLog(`corrective measure available ${e.target.value} — 14-day final clock started`); void postAction('fix-available', { date: e.target.value }); } }} />
                        : <RichEditor
                            compact
                            html={USE_FIXTURES ? (f.val ? `<p>${f.val}</p>` : '') : seedHtml(q16IdFor(f) ? q16Local[q16IdFor(f)!] : undefined)}
                            placeholder={f.hint}
                            onTextChange={q16IdFor(f) ? (plain, done) => saveQ16(q16IdFor(f)!, plain, done) : undefined}
                          />}
                    </Field>
                  ))}
                </div>
              </div>
            )}

            {tab === 'q16' && (
              <div className="tabpane on">
                <div className="section-head">
                  <h3>ENISA SRP · Q16 fields</h3>
                  <span className="aside" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                    <Badge kind="out">informative</Badge>
                    <Btn sm onClick={() => setQ16Editing((e) => !e)}>{q16Editing ? 'Done' : 'Edit fields'}</Btn>
                  </span>
                </div>
                <p className="hint" style={{ margin: 0 }}>
                  These are the ENISA SRP form fields. Submission is <b>manual web-form entry</b> (EU Login) — you type these into the portal; ENISA takes no file upload. Product identification only, never the SBOM. Use “Download as .md” for a printable fill guide.
                  Obligation per stage: <b className="mono">X</b> mandatory · <b className="mono">C</b> carried · <b className="mono">O</b> optional · <b className="mono">I</b> if available · <b className="mono">A</b> automated.
                </p>
                <div className="q16-wrap">
                  <table className="q16">
                    <thead><tr><th>#</th><th>Field</th><th className="oc">24h</th><th className="oc">72h</th><th className="oc">Final</th><th>Source</th><th>Value</th></tr></thead>
                    <tbody>
                      {q16.map((f) => (
                        <tr key={f.n}>
                          <td className="q16-n">{f.n}</td>
                          <td className="q16-l">{f.label}</td>
                          <td className="oc"><span className={`ob ob-${f.h24.toLowerCase()}`}>{f.h24}</span></td>
                          <td className="oc"><span className={`ob ob-${f.h72.toLowerCase()}`}>{f.h72}</span></td>
                          <td className="oc"><span className={`ob ob-${f.fin.toLowerCase()}`}>{f.fin}</span></td>
                          <td>{f.src === 'auto' ? <span className="src-auto">Auto</span> : <span className="src-you">You</span>}</td>
                          <td className="q16-v">
                            {q16Editing && f.src === 'you'
                              ? <input
                                  className="input" placeholder={f.hint ?? ''} data-testid={`q16-input-${f.n}`}
                                  value={q16Local[f.n] ?? f.val ?? ''}
                                  onChange={(e) => saveQ16(f.n, e.target.value, false)}
                                  onBlur={(e) => saveQ16(f.n, e.target.value, true)}
                                />
                              : f.val
                                ? <span style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}>
                                    <span className={f.mono ? 'mono' : undefined}>{f.val}</span>
                                    <button
                                      type="button" className="copy-btn" data-testid={`copy-${f.n}`} title="Copy — paste into the SRP form"
                                      onClick={() => { void navigator.clipboard?.writeText(f.val!).then(() => showToast(`Copied field ${f.n} — paste into the SRP form`)); }}
                                    >⧉ copy</button>
                                  </span>
                                : <span className="q16-hint">{f.hint ?? 'you complete this'}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {typeof pkg?.fieldSheet === 'string' && (
                  <div className="toolbar" style={{ marginTop: 12 }}>
                    <Btn sm data-testid="download-doc" data-fname={FIELD_SHEET_NAME} onClick={() => downloadDoc(FIELD_SHEET_NAME)}>
                      Download as .md
                    </Btn>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Actions — C-8: Archive (not Delete) for KEV/sent */}
          <div className="toolbar">
            <Btn variant="primary" onClick={() => void generate()}>{generated ? 'Regenerate SRP report' : 'Generate SRP report'}</Btn>
            <Btn onClick={() => setVexOpen((o) => !o)} data-testid="vex-toggle">{ev.vexStatus ? `VEX: ${VEX_STATUSES.find((v) => v.value === ev.vexStatus)?.label ?? ev.vexStatus} ✓ — edit` : 'Set as not affected'}</Btn>
            {(ev.isKev || status === 'Sent' || ev.notifiedAt) ? <Btn data-testid="archive-incident" onClick={() => setArchiveOpen(true)}>Archive</Btn> : <Btn variant="danger" data-testid="delete-incident" onClick={() => setDeleteOpen(true)}>Delete</Btn>}
          </div>

          {/* VEX editor — KEV audited not-affected path with Manager sign-off (C-3) */}
          {vexOpen && <VexPanel ev={ev} isManager={canManage(session.role)} showToast={showToast} onClose={() => setVexOpen(false)} onSubmit={async ({ vexStatus, code, just, impact, remed }) => {
            // LIVE: persist the VEX against this event (server resolves the finding + enforces the KEV Art.14 guard).
            if (!USE_FIXTURES) {
              const r = await postAction('vex', {
                vexStatus,
                justification: just,
                ...(code ? { justificationCode: code } : {}),
                ...(impact.trim() ? { impactStatement: impact } : {}),
                ...(remed.trim() ? { remediation: remed } : {}),
              });
              if (!r) return false; // server refused (e.g. KEV guard) — postAction surfaced it; keep the panel open
            }
            addLog(vexStatus === 'not_affected' && ev.isKev
              ? `KEV ${ev.cveId} → not affected (Manager sign-off) — VEX attached to the Art.14 event; decision logged`
              : `VEX ${ev.cveId} → ${vexStatus}`);
            const label = VEX_STATUSES.find((v) => v.value === vexStatus)?.label ?? vexStatus;
            showToast(`Saved — recorded as “${label}”.`);
            setVexOpen(false);
            if (!USE_FIXTURES) reload(); // pull the saved VEX back so the badge/banner update immediately
            return true;
          }} />}

          {/* ENISA SRP submission — MANUAL web-form fill guide (ENISA takes no file upload) */}
          {generated && (
            <Card style={{ padding: 20 }}>
              <div className="section-head"><h3>ENISA SRP submission — {stage.label}</h3><Badge kind="out">manual web-form entry</Badge></div>
              <p className="hint" style={{ margin: '8px 0 12px' }}>
                At launch the SRP is a <b>manual web form</b> (EU Login) — no API, no upload. Use the <b>fill guide</b> to copy each field straight into the form.
                The <b>CSAF</b> file reflects the machine format ENISA/CRA is moving toward (and doubles as your published advisory) — keep it for the future SRP API; don't upload it now.
              </p>
              {genError !== null ? (
                <div className="hint" data-testid="generate-error" style={{ display: 'flex', gap: 12, alignItems: 'center', color: 'var(--critical)' }}>
                  <span>Report generation failed — {genError}. Retry.</span>
                  <Btn sm variant="primary" onClick={() => void generate()}>Retry</Btn>
                </div>
              ) : (
                <div className="doc-list">
                  {pkg ? (() => {
                    const groups = groupDocs(pkg);
                    const missing = missingNow; // live view — shrinks as the user types
                    const row = (fname: string, label: string) => (
                      <div className="doc" key={fname}>
                        <span className="doc-ic">▤</span>
                        <div><div className="doc-n">{label}</div><div className="doc-m mono">{fname}</div></div>
                        <div className="toolbar" style={{ margin: 0 }}>
                          <Btn sm data-testid="download-doc" data-fname={fname} onClick={() => downloadDoc(fname)}>Download</Btn>
                        </div>
                      </div>
                    );
                    const CSAF_LABELS: Record<string, string> = {
                      'csaf-advisory.json': 'CSAF 2.0 security advisory',
                      'csaf-vex.json': 'CSAF 2.0 VEX',
                      'cyclonedx-vex.json': 'CycloneDX VEX',
                    };
                    return (
                      <>
                        {missing.length > 0 && (
                          <div className="hint" data-testid="missing-mandatory" style={{ color: 'var(--critical)', margin: '4px 0 10px' }}>
                            ⚠ Not ready to submit — mandatory for this level, still empty:{' '}
                            {missing.map((m) => `${m.field} (${m.label})`).join(' · ')}
                            <span style={{ color: 'var(--muted)' }}> — fill them in “What to fill” or the Q16 tab; the list updates as you type.</span>
                          </div>
                        )}
                        {/* 1 · Manual entry NOW — the paste-friendly fill guide for the web form. */}
                        <SectionHead title="Enter now — manual web form" aside="copy each value into the SRP portal (EU Login)" />
                        {typeof pkg.fieldSheet === 'string'
                          ? row(FIELD_SHEET_NAME, `Fill guide — ${stage.label} · copy each field into the SRP web form`)
                          : null}
                        <div className="toolbar" style={{ margin: '8px 0 0' }}>
                          <Btn sm variant="primary" data-testid="send-srp" onClick={() => onStatusChange('Sent')}>
                            Mark as sent to ENISA SRP…
                          </Btn>
                        </div>

                        {/* 2 · Future machine format — CSAF (the direction ENISA/CRA is moving) + your published advisory. NOT uploaded at launch. */}
                        {groups.users.length > 0 && (
                          <>
                            <div style={{ marginTop: 14 }}>
                              <SectionHead title="Machine format — CSAF (future / your advisory)" aside="the coming SRP standard; also your published advisory — not uploaded at launch" />
                            </div>
                            {groups.users.map((f) => row(f, CSAF_LABELS[f] ?? f))}
                          </>
                        )}

                        {/* 3 · Other authorities (kind=incident): NIS2 Art.23 CSIRT draft — a SEPARATE obligation. */}
                        {groups.other.length > 0 && (
                          <>
                            <div style={{ marginTop: 14 }}>
                              <SectionHead title="Other authorities — not ENISA" aside="NIS2 Art.23 — separate obligation, goes to the CSIRT" />
                            </div>
                            {groups.other.map((f) => row(f, f === 'nis2-draft.json' ? 'NIS2 Art.23 early-warning draft (CSIRT)' : f))}
                          </>
                        )}
                      </>
                    );
                  })() : USE_FIXTURES ? (
                    // Fixtures-only demo row — the ENISA fill guide (.md), so Download works offline.
                    <div className="doc">
                      <span className="doc-ic">▤</span>
                      <div><div className="doc-n">ENISA SRP fill guide — {stage.label}</div><div className="doc-m mono">{FIELD_SHEET_NAME}</div></div>
                      <div className="toolbar" style={{ margin: 0 }}>
                        <Btn
                          sm data-testid="download-doc" data-fname={FIELD_SHEET_NAME}
                          onClick={() => downloadDoc(FIELD_SHEET_NAME, { fieldSheet: `# ENISA SRP submission — ${stage.label}\n\nManual web-form entry (EU Login). Open the SRP portal and type each field below into the form.\nENISA accepts no file upload — this sheet is your fill guide.\n\n| # | Field | Value |\n|---|---|---|\n| 8 | Product | ${ev.productName} ${ev.versionLabel} |\n| 12 | Title | Actively exploited ${ev.cveId} in ${ev.productName} ${ev.versionLabel} |\n` })}
                        >Download</Btn>
                        <Btn sm variant="primary" data-testid="send-srp" onClick={() => onStatusChange('Sent')}>Mark as sent to ENISA SRP…</Btn>
                      </div>
                    </div>
                  ) : (
                    <div className="hint">Documents were generated in a previous session — use “Regenerate SRP report” to rebuild them.</div>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Art.14(8) — informing impacted users (C-4), from 72h on */}
          {from72 && (
            <Card style={{ padding: '16px 18px' }}>
              <div className="section-head"><h3>After notifying ENISA — your users</h3><Badge kind="warn">Art.14(8) · manufacturer duty</Badge></div>
              <p className="hint" style={{ margin: '8px 0 10px' }}>Sending to ENISA does not close this obligation: impacted users must be informed of the vulnerability and the corrective measures they can take, without undue delay.</p>
              <label className="ck">
                <input type="checkbox" checked={usersInformed} onChange={(e) => { setUsersInformed(e.target.checked); if (e.target.checked) { addLog('marked impacted users informed — Art.14(8)'); void postAction('users-informed'); } }} />
                <span>Impacted users have been informed (advisory published / customers notified)</span>
              </label>
            </Card>
          )}
        </div>

        {/* Action log */}
        <Card className="panel">
          <div className="panel-head"><h3>Action log</h3><Badge kind="out">immutable</Badge></div>
          <div style={{ padding: '14px 18px' }} data-testid="action-log">
            {log.map((l, i) => (
              <div key={i} className="logrow"><span className="when">{l.when}</span><span><b>{l.who}</b> · {l.what}</span></div>
            ))}
            {!acked && <div className="logrow"><span className="when">—</span><span style={{ color: 'var(--muted)' }}>awaiting: acknowledge · fill report · Status → Sent</span></div>}
          </div>
        </Card>
      </div>
    </>
  );
}

// ── VEX editor: KEV not-affected is the audited Art.14 path with Manager sign-off (REVIEW-FIXES C-3) ──
interface VexSubmit { vexStatus: string; code: string; just: string; impact: string; remed: string }
function VexPanel({ ev, isManager, onClose, onSubmit, showToast }: { ev: Art14Row; isManager: boolean; onClose: () => void; onSubmit: (data: VexSubmit) => Promise<boolean>; showToast: (m: string) => void }) {
  // Pre-fill EVERY field from the already-recorded VEX so a re-opened panel shows
  // the saved data, not blanks (user report 2026-07-21: "sigue sin guardar los datos"
  // — the write persisted but the panel reopened empty, so it looked unsaved).
  // The stored justification carries an audit seam ([Art.14 event … · decision by …]);
  // strip it for editing so a re-save doesn't stack a second seam.
  const stripSeam = (s: string | null | undefined) => (s ?? '').replace(/\s*\[Art\.14 event[^\]]*\]\s*$/, '');
  const [vexStatus, setVexStatus] = useState(ev.vexStatus ?? '');
  const [code, setCode] = useState(ev.vexJustificationCode ?? '');
  const [just, setJust] = useState(stripSeam(ev.vexJustification));
  const [impact, setImpact] = useState(ev.vexImpact ?? '');
  const [remed, setRemed] = useState(ev.vexRemediation ?? '');
  const [mgrAck, setMgrAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const recordedLabel = VEX_STATUSES.find((v) => v.value === ev.vexStatus)?.label ?? null;
  const na = vexStatus === 'not_affected';
  const kev = ev.isKev;

  // What is still missing — so the button is NEVER a silent dead-end (user report
  // 2026-07-20: "button does not work" = it was disabled with no feedback).
  function whatsMissing(): string | null {
    if (!vexStatus) return 'Pick a VEX status (Not affected / Affected / Fixed / Under investigation) first.';
    const need: string[] = [];
    if (just.trim().length < 10) need.push('a justification (at least 10 characters)');
    if (na) {
      if (!code) need.push('a CISA justification code');
      if (impact.trim().length < 10) need.push('an impact statement (at least 10 characters)');
      if (kev && !isManager) return 'A KEV “not affected” must be signed off by a Manager — switch to the OEM Manager role to continue.';
      if (kev && !mgrAck) need.push('the Manager sign-off checkbox at the bottom');
    }
    return need.length ? `Before writing this VEX, add: ${need.join('; ')}.` : null;
  }

  return (
    <div className="vex-panel">
      <div className="vex-q">Does <span className="mono">{ev.cveId}</span> affect {ev.productName} {ev.versionLabel}?</div>
      {recordedLabel && (
        <div className="vex-recorded" data-testid="vex-recorded" style={{ margin: '0 0 12px', padding: '9px 12px', borderRadius: 8, background: 'color-mix(in srgb, var(--safe, #16a34a) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--safe, #16a34a) 35%, transparent)', fontSize: 'calc(13px*var(--fs))' }}>
          ✓ Saved — this finding is currently recorded as <b>{recordedLabel}</b>{ev.vexJustification ? <>: <span className="mono">{ev.vexJustification.replace(/\s*\[Art\.14 event.*$/, '')}</span></> : null}. Submitting again updates it (a new revision).
        </div>
      )}
      {na && kev && (
        <div className="vex-guard">
          <b>{ev.cveId} is a KEV — actively exploited in the wild.</b> Declaring it <i>not affected</i> states that {ev.productName} {ev.versionLabel} contains no exploitable copy of this vulnerability, so <b>no Art.14 obligation arises</b>. This is a formal, audited statement: a CISA justification code, an impact statement and a <b>Manager sign-off</b> are required; the VEX is attached to the Art.14 event and the decision is permanently logged.
        </div>
      )}
      <div className="vex-row">
        <span className="vex-label">VEX status</span>
        <div className="vex-opts" role="radiogroup">
          {VEX_STATUSES.map((v) => (
            <label key={v.value} className="vopt"><input type="radio" name="vexstat" value={v.value} checked={vexStatus === v.value} onChange={() => setVexStatus(v.value)} /><span>{v.label}</span></label>
          ))}
        </div>
      </div>
      {na && (
        <div className="vex-row">
          <span className="vex-label">CISA justification <span className="req">*</span> <span className="hint">— the code that reaches the regulator (no default)</span></span>
          <select className="select" value={code} onChange={(e) => setCode(e.target.value)}>
            <option value="">Select a justification…</option>
            {CISA_JUSTIFICATIONS.map((j) => <option key={j.code} value={j.code}>{j.code} — {j.label}</option>)}
          </select>
        </div>
      )}
      <div className="vex-row">
        <span className="vex-label">Justification <span className="req">*</span> <span className="hint">— a named reason; no dismissal without one</span></span>
        <textarea className="input" rows={2} value={just} onChange={(e) => setJust(e.target.value)} placeholder="e.g. vulnerable code path not reachable in our firmware build" />
      </div>
      {na && (
        <div className="vex-row">
          <span className="vex-label">Impact statement <span className="req">*</span></span>
          <textarea className="input" rows={2} value={impact} onChange={(e) => setImpact(e.target.value)} placeholder="e.g. the vulnerable function is excluded from this build (.config line 2847)" />
        </div>
      )}
      {vexStatus === 'fixed' && (
        <div className="vex-row">
          <span className="vex-label">Remediation <span className="hint">(optional)</span></span>
          <textarea className="input" rows={2} value={remed} onChange={(e) => setRemed(e.target.value)} placeholder="e.g. patched in 3.4.1 shipped to all placed-on-market units" />
        </div>
      )}
      {na && kev && (
        <label className="ck" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={mgrAck} disabled={!isManager} onChange={(e) => setMgrAck(e.target.checked)} />
          <span>As Manager I confirm this product-version is not affected; this records the determination on the Art.14 event (VEX attached as evidence) and is logged in the audit trail. The obligation stays open until you mark it notified/complete.{!isManager && <em style={{ color: 'var(--muted)' }}> (switch to OEM Manager to sign off)</em>}</span>
        </label>
      )}
      <div className="toolbar" style={{ marginTop: 6 }}>
        <Btn variant="primary" disabled={busy} onClick={async () => {
          const miss = whatsMissing();
          if (miss) { showToast(miss); return; } // tell the user WHAT to fix, never nothing
          setBusy(true);
          const done = await onSubmit({ vexStatus, code, just, impact, remed });
          if (!done) setBusy(false); // on success the parent unmounts this panel — avoid setState-after-unmount
        }}>
          {busy ? 'Writing…' : na ? 'Write not-affected VEX' : 'Write VEX statement'}
        </Btn>
        <Btn onClick={onClose}>Cancel</Btn>
      </div>
    </div>
  );
}
